"use client"

import {
    Building2,
    ChevronRight,
    Compass,
    Crown,
    Quote,
    Rocket,
    Sparkles,
    Terminal
} from "lucide-react"
import { motion, useScroll, useSpring, useTransform } from "motion/react"
import { useRef, useState } from "react"

import { cn } from "@/core/utils/utils"

const milestones = [
    {
        year: "2016",
        era: "Foundations",
        title: "Software Engineering Core",
        narrative: "Deep focus on computer science fundamentals, algorithm efficiency, and backend architecture. Built scalable APIs, reliable databases, and high-performance server utilities.",
        metrics: ["Production Web Apps", "Database Architecture", "Cloud Deployments"],
        insight: "Simplicity and discipline in code always pay long-term dividends.",
        icon: Terminal,
        gradient: "from-slate-500 to-slate-400",
        glowColor: "rgba(148, 163, 184, 0.3)",
        accentColor: "slate",
    },
    {
        year: "2018",
        era: "Full-Stack Velocity",
        title: "Modern Web Platforms",
        narrative: "Shipped enterprise web applications and customer-facing products with React, Node.js, and modern cloud infrastructure—focusing on sub-second page loads and seamless UX.",
        metrics: ["15+ Applications Shipped", "React & Node.js", "CI/CD Automation"],
        insight: "Great user experiences require rock-solid engineering under the hood.",
        icon: Rocket,
        gradient: "from-blue-500 to-cyan-400",
        glowColor: "rgba(59, 130, 246, 0.3)",
        accentColor: "blue",
    },
    {
        year: "2021",
        era: "Systems & Leadership",
        title: "Distributed Architecture",
        narrative: "Led engineering teams and designed high-throughput systems, reducing latency by 40% and managing high-concurrency data pipelines for global platforms.",
        metrics: ["Team Technical Lead", "40% Latency Reduction", "FinTech Scale"],
        insight: "The best architectures make continuous iteration effortless.",
        icon: Building2,
        gradient: "from-violet-500 to-purple-400",
        glowColor: "rgba(139, 92, 246, 0.3)",
        accentColor: "violet",
    },
    {
        year: "2023",
        era: "Frontier AI",
        title: "AI Systems & Intelligent Agents",
        narrative: "Architecting autonomous AI agent swarms, vector retrieval engines, and real-time LLM integrations that automate complex business workflows securely.",
        metrics: ["Multi-Agent Pipelines", "Zero-Data Retention", "Enterprise Integrations"],
        insight: "AI creates maximum value when embedded deeply into existing operations.",
        icon: Crown,
        gradient: "from-amber-500 to-orange-400",
        glowColor: "rgba(245, 158, 11, 0.3)",
        accentColor: "amber",
    },
    {
        year: "Today",
        era: "Engineering Partner",
        title: "Dedicated Technical Partner",
        narrative: "Partnering directly with founders and companies to build, launch, and scale AI-native products, custom SaaS applications, and modern automated workflows.",
        metrics: ["Global Clients", "Fixed $3k/mo Retainer", "Direct Collaboration"],
        insight: "Delivering senior-grade execution with zero agency overhead.",
        icon: Compass,
        gradient: "from-emerald-500 to-teal-400",
        glowColor: "rgba(16, 185, 129, 0.3)",
        accentColor: "emerald",
    },
]

export default function JourneyTimeline({ className }: { className?: string }) {
    const containerRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    })

    const smoothProgress = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    })

    const pathLength = useTransform(smoothProgress, [0.1, 0.9], [0, 1])

    return (
        <section 
            id="journey" 
            className={cn("relative py-32 overflow-hidden bg-background", className)}
            ref={containerRef}
        >
            {/* Premium Background Effects */}
            <div className="absolute inset-0">
                {/* Gradient Orbs */}
                <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-violet-500/5 rounded-full blur-[150px] pointer-events-none" />
                <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/3 rounded-full blur-[200px] pointer-events-none" />
                
                {/* Grid Pattern */}
                <div 
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage: `
                            linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)
                        `,
                        backgroundSize: '60px 60px',
                        maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)'
                    }}
                />

                {/* Floating Particles */}
                <FloatingParticles />

                {/* Top Edge Glow */}
                <div className="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-white/10 to-transparent" />
                
                {/* Bottom Edge Fade */}
                <div className="absolute bottom-0 inset-x-0 h-32 bg-linear-to-t from-[#050505] to-transparent pointer-events-none" />
            </div>

            <div className="container px-4 md:px-6 relative z-10">
                {/* Section Header */}
                <div className="flex flex-col items-center justify-center text-center mb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="relative"
                    >
                        {/* Main Title */}
                        <h2 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
                            <span className="text-white">The </span>
                            <span className="bg-linear-to-r from-slate-300 via-white to-slate-300 bg-clip-text text-transparent animate-shimmer bg-size-[200%_100%]">
                                Trajectory
                            </span>
                        </h2>

                        {/* Subtitle */}
                        <p className="mt-6 text-slate-400 text-lg md:text-xl max-w-[700px] mx-auto leading-relaxed">
                            From debugging scripts to architecting systems.
                            <span className="block mt-2 text-white/70 font-medium">
                                Each milestone—a lesson compounded.
                            </span>
                        </p>

                        {/* Decorative Glow */}
                        <div className="absolute -inset-x-20 -top-10 bottom-0 bg-linear-to-b from-violet-500/10 to-transparent blur-[100px] rounded-full pointer-events-none -z-10" />
                    </motion.div>
                </div>

                {/* Timeline Container */}
                <div className="relative max-w-5xl mx-auto">
                    {/* The Ascending Path - SVG Line */}
                    <svg 
                        className="absolute left-8 md:left-1/2 top-0 h-full w-[2px] md:-translate-x-1/2 pointer-events-none"
                        viewBox="0 0 2 100"
                        preserveAspectRatio="none"
                    >
                        {/* Background Track */}
                        <line 
                            x1="1" y1="0" x2="1" y2="100" 
                            stroke="rgba(255,255,255,0.1)" 
                            strokeWidth="2"
                            vectorEffect="non-scaling-stroke"
                        />
                        {/* Animated Progress */}
                        <motion.line 
                            x1="1" y1="0" x2="1" y2="100" 
                            stroke="url(#trajectoryGradient)" 
                            strokeWidth="2"
                            vectorEffect="non-scaling-stroke"
                            style={{ pathLength }}
                        />
                        <defs>
                            <linearGradient id="trajectoryGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#94a3b8" />
                                <stop offset="25%" stopColor="#3b82f6" />
                                <stop offset="50%" stopColor="#8b5cf6" />
                                <stop offset="75%" stopColor="#f59e0b" />
                                <stop offset="100%" stopColor="#10b981" />
                            </linearGradient>
                        </defs>
                    </svg>

                    {/* Milestone Cards */}
                    <div className="space-y-16 md:space-y-24">
                        {milestones.map((milestone, index) => (
                            <MilestoneCard 
                                key={milestone.year}
                                milestone={milestone}
                                index={index}
                                isEven={index % 2 === 0}
                            />
                        ))}
                    </div>
                </div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="mt-24 text-center"
                >
                    <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md">
                        <span className="text-slate-400">Ready to write the next chapter?</span>
                        <a 
                            href="/contact" 
                            className="inline-flex items-center gap-1 text-white font-medium hover:text-emerald-400 transition-colors group"
                        >
                            Let&apos;s talk
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </a>
                    </div>
                </motion.div>
            </div>
        </section>
    )
}

// Floating Particles Component
function FloatingParticles() {
    const [particles] = useState<Array<{
        id: number
        size: number
        x: number
        y: number
        duration: number
        delay: number
        xOffset: number
    }>>(() => {
        // Initialize particles on first render to avoid setState in effect
        if (typeof window === "undefined") {
            return []
        }
        return Array.from({ length: 20 }, (_, i) => ({
            id: i,
            size: Math.random() * 3 + 1,
            x: Math.random() * 100,
            y: Math.random() * 100,
            duration: Math.random() * 10 + 15,
            delay: Math.random() * 5,
            xOffset: Math.random() * 20 - 10,
        }))
    })

    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {particles.map((particle) => (
                <motion.div
                    key={particle.id}
                    className="absolute rounded-full bg-white/20"
                    style={{
                        width: particle.size,
                        height: particle.size,
                        left: `${particle.x}%`,
                        top: `${particle.y}%`,
                    }}
                    animate={{
                        y: [0, -30, 0],
                        x: [0, particle.xOffset, 0],
                        opacity: [0.1, 0.4, 0.1],
                    }}
                    transition={{
                        duration: particle.duration,
                        repeat: Infinity,
                        delay: particle.delay,
                        ease: "easeInOut",
                    }}
                />
            ))}
        </div>
    )
}

function MilestoneCard({ 
    milestone, 
    index, 
    isEven 
}: { 
    milestone: typeof milestones[0]
    index: number
    isEven: boolean 
}) {
    const [isHovered, setIsHovered] = useState(false)
    const Icon = milestone.icon

    const accentColors: Record<string, { border: string; bg: string; text: string; glow: string }> = {
        slate: { 
            border: "border-slate-500/30 hover:border-slate-400/50", 
            bg: "bg-slate-500/10", 
            text: "text-slate-400",
            glow: "shadow-slate-500/20"
        },
        blue: { 
            border: "border-blue-500/30 hover:border-blue-400/50", 
            bg: "bg-blue-500/10", 
            text: "text-blue-400",
            glow: "shadow-blue-500/20"
        },
        violet: { 
            border: "border-violet-500/30 hover:border-violet-400/50", 
            bg: "bg-violet-500/10", 
            text: "text-violet-400",
            glow: "shadow-violet-500/20"
        },
        amber: { 
            border: "border-amber-500/30 hover:border-amber-400/50", 
            bg: "bg-amber-500/10", 
            text: "text-amber-400",
            glow: "shadow-amber-500/20"
        },
        emerald: { 
            border: "border-emerald-500/30 hover:border-emerald-400/50", 
            bg: "bg-emerald-500/10", 
            text: "text-emerald-400",
            glow: "shadow-emerald-500/20"
        },
    }

    const colors = accentColors[milestone.accentColor]

    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
                "relative flex items-start gap-6 md:gap-0",
                isEven ? "md:flex-row" : "md:flex-row-reverse"
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Timeline Node */}
            <div className="absolute left-8 md:left-1/2 -translate-x-1/2 z-20">
                <motion.div
                    animate={{ 
                        scale: isHovered ? 1.15 : 1,
                        rotate: isHovered ? 3 : 0,
                    }}
                    transition={{ duration: 0.4, type: "spring", stiffness: 300 }}
                    className="relative"
                >
                    {/* Glow Ring */}
                    <motion.div
                        animate={{
                            opacity: isHovered ? 1 : 0.5,
                            scale: isHovered ? 1.2 : 1,
                        }}
                        className="absolute inset-0 rounded-2xl"
                        style={{
                            boxShadow: `0 0 ${isHovered ? 50 : 25}px ${milestone.glowColor}`,
                        }}
                    />
                    
                    {/* Icon Container */}
                    <div
                        className={cn(
                            "relative w-16 h-16 rounded-2xl border-2 flex items-center justify-center",
                            "bg-[#0a0a0a] backdrop-blur-xl",
                            colors.border,
                            "transition-all duration-300"
                        )}
                    >
                        {/* Inner Glow */}
                        <div className={cn(
                            "absolute inset-2 rounded-xl opacity-20",
                            colors.bg
                        )} />
                        <Icon className={cn("w-7 h-7 relative z-10", colors.text)} />
                    </div>
                    
                    {/* Pulse Ring */}
                    <motion.div
                        animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.3, 0, 0.3],
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        className={cn(
                            "absolute inset-0 rounded-2xl border",
                            colors.border.replace('hover:', '')
                        )}
                    />
                </motion.div>
            </div>

            {/* Content Card */}
            <div className={cn(
                "ml-24 md:ml-0 md:w-[45%]",
                isEven ? "md:pr-16" : "md:pl-16"
            )}>
                <motion.div
                    animate={{ 
                        y: isHovered ? -4 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    className={cn(
                        "relative p-6 md:p-8 rounded-2xl border",
                        "bg-white/2 backdrop-blur-xl",
                        colors.border,
                        "transition-all duration-300",
                        isHovered && `shadow-2xl ${colors.glow}`
                    )}
                    style={{
                        boxShadow: isHovered ? `0 25px 50px -12px ${milestone.glowColor}` : undefined
                    }}
                >
                    {/* Gradient Accent Line */}
                    <div 
                        className={cn(
                            "absolute top-0 left-6 right-6 h-[2px] rounded-full bg-linear-to-r opacity-60",
                            milestone.gradient
                        )}
                    />

                    {/* Era & Year */}
                    <div className="flex items-center gap-3 mb-4">
                        <span className={cn(
                            "px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase",
                            colors.bg,
                            colors.text
                        )}>
                            {milestone.year}
                        </span>
                        <span className="text-slate-500 text-sm font-medium tracking-wide">
                            {milestone.era}
                        </span>
                    </div>

                    {/* Title */}
                    <h3 className={cn(
                        "text-xl md:text-2xl font-bold mb-3 bg-linear-to-r bg-clip-text text-transparent",
                        milestone.gradient
                    )}>
                        {milestone.title}
                    </h3>

                    {/* Narrative */}
                    <p className="text-slate-400 text-sm md:text-base leading-relaxed mb-5">
                        {milestone.narrative}
                    </p>

                    {/* Metrics */}
                    <div className="flex flex-wrap gap-2 mb-5">
                        {milestone.metrics.map((metric, idx) => (
                            <span 
                                key={idx}
                                className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 font-medium"
                            >
                                {metric}
                            </span>
                        ))}
                    </div>

                    {/* Insight Quote */}
                    <div className={cn(
                        "flex items-start gap-3 p-4 rounded-xl",
                        colors.bg,
                        "border border-white/5"
                    )}>
                        <Quote className={cn("w-4 h-4 mt-0.5 shrink-0", colors.text)} />
                        <p className={cn("text-sm italic", colors.text)}>
                            &quot;{milestone.insight}&quot;
                        </p>
                    </div>

                    {/* Decorative Corner */}
                    <div className={cn(
                        "absolute bottom-4 right-4 w-8 h-8 opacity-10",
                        "border-r-2 border-b-2 rounded-br-lg",
                        colors.border.replace('hover:', '')
                    )} />
                </motion.div>
            </div>

            {/* Empty Space for Alternating Layout */}
            <div className="hidden md:block md:w-[45%]" />
        </motion.div>
    )
}
